import { useParams } from 'react-router-dom';
import { Chips, CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, CircularProgress, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { format } from 'date-fns';
import { capitalize, truncate } from 'lodash';
import { Dataset, DatasetTypeEnum, ProjectStatusEnum, ProjectTypeEnum, RoleEnum } from '~/api/types';
import { useGetProjectQuery } from '~/api/v2/hooks';
import { useWbsOrCostCenter } from '~/hooks';
import { InfoIcon } from '~/icons';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { DATE_FORMAT, getPriviligies } from '~/utils';
import { DatasetConnectionIcon } from '../components';
import { projectDetailsContainerStyles } from '../components/styles';

const ProjectDetailsTab = () => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const { data: project, isFetching, isError } = useGetProjectQuery({ projectId });
  const { displayValue: wbsOrCostCenter } = useWbsOrCostCenter({
    wbsCodeId: project?.wbs_code_id,
    costCenterId: project?.cost_center_id,
  });

  const user = useAppSelector(selectCurrentUser);
  const owner = project?.members?.find(({ role_id }) => RoleEnum.OWNER === role_id);
  const canEditProject =
    getPriviligies(project?.members?.find(({ id }) => id === user?.id)?.role_id)?.projectDetailsTab.canEdit ||
    getPriviligies(user?.role?.id)?.projectDetailsTab.canEdit;
  const isMember = !!project?.members?.find(({ id }) => id === user?.id);
  const datasetLabelLength = useMediaQuery('(max-width:1100px)') ? 20 : 40;

  const DatasetsList = ({ datasets }: { datasets?: Dataset[] }) => {
    if (!datasets?.length) return <Typography variant="paragraph">No datasets linked</Typography>;

    return (
      <Stack direction="column" spacing={1}>
        {datasets?.map((dataset) => (
          <Stack key={dataset.id} direction="row" alignItems="center" spacing={1}>
            <Chips
              alignment="left"
              chipsConfig={[{ label: truncate(dataset.name, { length: datasetLabelLength }), type: 'informative' }]}
            />
            {isMember && dataset.type !== DatasetTypeEnum.DB && (
              <DatasetConnectionIcon projectId={projectId} datasetId={dataset.id} />
            )}
          </Stack>
        ))}
      </Stack>
    );
  };

  if (isError) {
    return (
      <Typography variant="h5" p="6rem">
        Error loading project details
      </Typography>
    );
  }

  return (
    <Box sx={projectDetailsContainerStyles}>
      {isFetching ? (
        <CircularProgress size={70} sx={{ color: '#1010eb' }} />
      ) : (
        <Grid container spacing={3} alignSelf="center">
          <Grid size={{ xs: 3 }}>
            <Typography variant="h6">Name:</Typography>
          </Grid>
          <Grid size={{ xs: 9 }} alignSelf="center" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word' }}>
            <Typography variant="h6">{project?.name}</Typography>
          </Grid>

          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Project Description:
            </Typography>
          </Grid>
          <Grid size={{ xs: 9 }} alignSelf="center">
            <Typography variant="paragraph" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word' }}>
              {project?.description}
            </Typography>
          </Grid>

          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Project Status:
            </Typography>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Chips
              chipsConfig={[
                {
                  label: capitalize(project?.status),
                  type: project?.status === ProjectStatusEnum.ACTIVE ? 'confirm' : 'critical',
                },
              ]}
              alignment="left"
            />
          </Grid>

          <Grid size={{ xs: 2 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Created By:
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }} alignSelf="center">
            <Typography variant="paragraph">
              {project?.creator ? `${project?.creator?.first_name} ${project?.creator?.last_name}` : 'System'}
            </Typography>
          </Grid>

          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Project Members:
            </Typography>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Chips
              chipsConfig={[
                {
                  label: project?.members?.length
                    ? `${project.members.length} member${project.members.length > 1 ? 's' : ''}`
                    : 'Unknown',
                  type: 'informative',
                },
              ]}
              alignment="left"
            />
          </Grid>

          <Grid size={{ xs: 2 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Owner:
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }} alignSelf="center">
            <Typography variant="paragraph">
              {owner ? `${owner?.first_name} ${owner?.last_name}` : 'No owner'}
            </Typography>
          </Grid>

          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              WBS / Cost Center Code:
            </Typography>
          </Grid>
          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="paragraph">{wbsOrCostCenter || '—'}</Typography>
          </Grid>

          {project?.type === ProjectTypeEnum.PRODUCER ? (
            <>
              <Grid size={{ xs: 2 }} alignSelf="center">
                <Typography variant="h6" color="#0000008C">
                  Project Type:
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Chips
                  chipsConfig={[
                    {
                      label: project?.type + ' Project',
                      type: 'customSolid',
                      customColor: '#F8ECFF',
                      customIcon: <InfoIcon fill="#C121EB" />,
                    },
                  ]}
                  alignment="left"
                />
              </Grid>
            </>
          ) : (
            <Grid size={{ xs: 6 }} />
          )}

          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="h6" color="#0000008C">
              Created On:
            </Typography>
          </Grid>
          <Grid size={{ xs: 3 }} alignSelf="center">
            <Typography variant="paragraph">
              {project?.created ? format(new Date(project.created), DATE_FORMAT) : '—'}
            </Typography>
          </Grid>

          {project?.type === ProjectTypeEnum.PRODUCER ? (
            <>
              <Grid size={{ xs: 2 }}>
                <Typography variant="h6" color="#0000008C" mb={1}>
                  Source Datasets:
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <DatasetsList datasets={project.datasets} />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="h6" color="#0000008C">
                  Last Modified:
                </Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="paragraph">
                  {project?.updated ? format(new Date(project.updated), DATE_FORMAT) : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Typography variant="h6" color="#0000008C" mb={1}>
                  Destination Datasets:
                </Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <DatasetsList datasets={project.destination_datasets} />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 2 }}>
                <Typography variant="h6" color="#0000008C" mb={1}>
                  Associated Datasets:
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <DatasetsList datasets={project?.datasets} />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="h6" color="#0000008C">
                  Last Modified:
                </Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="paragraph">
                  {project?.updated ? format(new Date(project.updated), DATE_FORMAT) : '—'}
                </Typography>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12 }}>
            {canEditProject && (
              <CustomButton
                buttonType="outlined"
                label="Edit"
                onClick={() => {
                  dispatch(openModal({ type: ModalTypesEnum.EDIT_PROJECT }));
                }}
                disabled={project?.status !== ProjectStatusEnum.ACTIVE}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ProjectDetailsTab;
